import moment from "moment";
import momentTz from "moment-timezone";
import { startCase, toLower } from "lodash";

import { APPROVER_GROUPS, findApproverGroup } from "../ApprovalManagement/Constants";
import { panelDocVersionKey } from "../../Utils/Constant";
import { timezone } from "../../Utils/StringConstants";

/**
 * Work Order module — shared option lists and step definitions.
 *
 * Subsidiary/Division and Cost Centre are NOT declared here — they are read
 * from what Panel Contract has configured, via `usePanelOrgTree`. Only the
 * lists that are genuinely Work-Order-specific live below.
 */

const AUTHORITY_HINTS: Record<string, string> = {
  mdGceo: "Routes to R1-R3, then GCEO and MD where they are configured.",
  gcfo: "Routes to R1 and R2, then the finance/operations authority configured for this cost centre.",
  headOfSubsidiary: "Routes to R1 and R2, then the Head of Subsidiary.",
  apc: "Routes to the Airport Procurement Committee.",
};

/**
 * The (i) beside each approval authority on the Reviewers step — e-POD's own
 * wording (AddNewSPA's Approval Authority radios), word for word.
 */
const AUTHORITY_PTE: Record<string, string> = {
  mdGceo: "PTE:5 Mil to 10 Mil",
  gcfo: "PTE:(MA Sepang 3 Mil to 5 Mil and UTW,MAN,MASB 1 Mil to 5 Mil)",
  headOfSubsidiary: "below 3 Mil, Below 1 Mil(UTW,MAN)",
  apc: "for MASB APC only (Category A 500k and others 150k)",
};

/**
 * Status filter on the Work Order Approval list, per tab. /work-order/approve/list
 * takes the status CODE (`status=CREATED`), not the label shown here.
 */
export const approvalStatusOptions = {
  pending: [
    { label: "Created", value: "CREATED" },
    { label: "In Progress", value: "IN_PROGRESS" },
  ],
  approved: [
    { label: "In Progress", value: "IN_PROGRESS" },
    { label: "Completed", value: "COMPLETED" },
    { label: "Cancelled", value: "CANCELLED" },
    { label: "Rejected", value: "REJECTED" },
    { label: "Expired", value: "EXPIRED" },
  ],
};

/**
 * A work order's status as people read it. The API answers with codes —
 * a newly submitted work order comes back PENDING_REVIEW, which QA reads as
 * "Created". Codes not listed still read as words, never as a raw code.
 */
const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  /* Created until the first action is taken. v2.1 answers CREATED; older
     records carry PENDING_REVIEW. */
  CREATED: "Created",
  PENDING_REVIEW: "Created",
  /* Reviewers / approvers still to approve. */
  PANEL_PENDING: "In Progress",
  PENDING: "In Progress",
  PENDING_APPROVAL: "In Progress",
  IN_PROGRESS: "In Progress",
  PANEL_REJECTED: "Rejected",
  REJECTED: "Rejected",
  APPROVED: "Completed",
  COMPLETED: "Completed",
  EXPIRED: "Expired",
  VOID: "Voided",
  VOIDED: "Voided",
  CLOSED: "Closed",
  RETURNED: "Returned",
  CANCELLED: "Cancelled",
};

export const workOrderStatusLabel = (code?: string): string => {
  const key = String(code ?? "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  return STATUS_LABELS[key] ?? startCase(toLower(key));
};

/** A reviewer's / approver's step status, in the routing's own words: one who
    has acted reads "Approved" (the workflow service stores COMPLETED for them). */
const ROUTING_LABELS: Record<string, string> = {
  COMPLETED: "Approved",
  APPROVED: "Approved",
  IN_PROGRESS: "In Progress",
  PENDING: "Pending",
  REJECTED: "Rejected",
};
export const routingStatusLabel = (code?: string): string => {
  const key = String(code ?? "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  return ROUTING_LABELS[key] ?? startCase(toLower(key));
};

/** Has this step in the routing approved? */
export const hasApproved = (code?: string): boolean =>
  ["APPROVED", "COMPLETED"].includes(String(code ?? "").trim().toUpperCase());

/** Dates read DD-MM-YYYY, as the form takes them; records hold ISO. */
export const displayDate = (value?: string): string =>
  value && moment(value).isValid() ? moment(value).format("DD-MM-YYYY") : value ?? "";

/** Date and time a reviewer acted, in Malaysia time (Asia/Kuala_Lumpur) as
    e-POD and this module's PDFs show it — not the browser's own time zone. */
export const displayDateTime = (value?: string): string =>
  value && moment(value).isValid() ? momentTz(value).tz(timezone).format("DD-MM-YYYY hh:mm A") : value ?? "";

/** The approach's name, not the code a record stores (NORMAL_ROTATION). */
export const approachLabel = (value?: string): string =>
  procurementApproaches.find((entry) => entry.value === value)?.label ?? value ?? "";

/** The Work Order list's Status filter: the label shown, the code sent. */
export const workOrderStatus = [
  { label: "Created", value: "CREATED" },
  { label: "Draft", value: "DRAFT" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Cancelled", value: "CANCELLED" },
];

export const workOrderRegion = ["Northern", "Central & Southern", "East Coast", "Sarawak", "Sabah"];

export const workOrderContractCategory = ["Term Contract", "Ad-hoc Contract", "Panel Contract"];

/** Wizard step order — index is the `current` passed to antd <Steps />. */
export const WORK_ORDER_STEPS = {
  PROJECT_INFORMATION: 0,
  SOR: 1,
  PROJECT_DOCUMENTS: 2,
  APPROVERS: 3,
  REVIEW_SUBMIT: 4,
};

/** Icon per step, mirroring the `stepItems` shape used by AddNewSPA. */
export const workOrderStepIcons = [
  "mdi:form",
  "mdi:hand-coin-outline",
  "mdi:file-document-outline",
  "mdi:file-sign",
  "mdi:application-edit-outline",
];

export const workOrderStepTitles = [
  "Project Information",
  "SOR",
  "Project Documents",
  // Step 4 collects reviewers AND approvers; the design labels it "Reviewers".
  "Reviewers",
  "Review & Submit",
];

/**
 * Procurement approaches offered on step 1 (vendor list depends on this).
 *
 * Normal is always available. Emergency is the one Manage Catalog -> Settings
 * governs, so only it is filtered on that toggle.
 */
export const procurementApproaches = [
  { value: "NORMAL_ROTATION", label: "Normal (Rotation Basis)" },
  { value: "EMERGENCY", label: "Emergency (Rotation/Selective)" },
];

/**
 * Approval authority groups, taken from APPROVER_GROUPS so the chain and what
 * can be configured cannot drift apart.
 *
 * `mandatory` designations are always steps in the chain. `optional` ones join
 * it only when an approver is configured for them — that is how one cost
 * centre routes to a CFO and another to a CAO under the same authority, and
 * why a fixed list of designations was never going to be right here.
 */
export const approvalAuthorities = APPROVER_GROUPS.map((group) => ({
  key: group.key,
  authorityGroup: group.authorityGroup,
  label: group.label,
  hint: AUTHORITY_HINTS[group.key] ?? "",
  pte: AUTHORITY_PTE[group.key],
  mandatory: group.mandatory,
  optional: group.optional,
  /** Every designation this authority can route to. */
  designations: group.designations,
}));

/**
 * A reviewer captured on step 4.
 *
 * `id` is only set when the row came back from `GET /workflow/get/reviewers/list`
 * on an existing work order — `PUT /workflow/edit-reviewer` needs it to update
 * the row in place rather than adding a duplicate.
 */
export interface ReviewerEntry {
  key: string;
  id?: number;
  name: string;
  email: string;
}

/** An approver row — designation is fixed by the chosen authority group. */
export interface ApproverEntry {
  key: string;
  id?: number;
  designation: string;
  name: string;
  email: string;
}

/** `role` values the workflow routing list uses to tell the two apart. */
export const ROUTING_ROLE = {
  REVIEWER: "REVIEWER",
  APPROVER: "APPROVER",
};

/** "Additional Document Details" row on step 3. */
/**
 * A wizard step rendered in place on the details page (e-POD's edit pattern):
 * its footer becomes Cancel / Save, and Save does what Next would.
 */
export interface InlineEdit {
  onCancel: () => void;
  isSaving?: boolean;
}

export interface DocumentLinkEntry {
  key: string;
  documentName: string;
  documentLink: string;
}

/**
 * What is wrong with each Additional Document row, by row key: a link that is
 * not http(s), or a name / link already on an earlier row (compared ignoring
 * case, edge spaces and a trailing "/"). Shared by the wizard and the detail
 * page's Edit Additional Document Link.
 */
export const documentLinkErrors = (links: DocumentLinkEntry[]) => {
  const same = (value?: string) => String(value ?? "").trim().toLowerCase().replace(/\/+$/, "");
  const invalid = (value: string) => {
    try {
      const url = new URL(value);
      return url.protocol !== "http:" && url.protocol !== "https:";
    } catch {
      return true;
    }
  };
  const errors: Record<string, { name?: string; link?: string }> = {};
  links.forEach((row, index) => {
    const earlier = links.slice(0, index);
    const name = same(row.documentName);
    const link = String(row.documentLink ?? "").trim();
    const entry: { name?: string; link?: string } = {};
    if (name && earlier.some((other) => same(other.documentName) === name)) {
      entry.name = "This document name is already added.";
    }
    if (link && invalid(link)) entry.link = "Enter a valid link.";
    else if (link && earlier.some((other) => same(other.documentLink) === same(link))) {
      entry.link = "This document link is already added.";
    }
    if (entry.name || entry.link) errors[row.key] = entry;
  });
  return errors;
};

/** Ceiling for the summed SOR line amounts (see the SOR step warning). */
export const WORK_ORDER_MAX_AMOUNT = 3_000_000;

export interface SorItem {
  key: string;
  sorId: string;
  itemName: string;
  description: string;
  /** Unit of measure, from the SOR ID in Manage Catalog. */
  uom?: string;
  quantity: number | string;
  rate: number | string;
  amount: number | string;
}

export interface WorkOrderFormValues {
  /** Template the work order is raised against — required by /work-order/create. */
  templateId: string;
  requesterName: string;
  requesterEmail: string;
  dateRequest: string;
  jobTitle: string;
  subsidiary: string;
  costCentre: string;
  department: string;
  prNo: string;
  background: string;
  scopeOfWork: string;
  budgetStatus: string;
  commencementDate: string;
  completionDate: string;
  /*
   * The four reference pickers below hold the record's ID, because that is
   * what the cascades need: sub regions are fetched by regionId and vendors by
   * regionId + approach. An id is not showable though, so each carries a
   * companion *Name captured at selection time — Review & Submit reads those,
   * and the payload sends both halves.
   */
  contractCategory: string;
  contractCategoryName: string;
  region: string;
  regionName: string;
  subRegion: string;
  subRegionName: string;
  procurementApproach: string;
  vendor: string;
  vendorName: string;
}

export const workOrderInitialValues: WorkOrderFormValues = {
  templateId: "",
  requesterName: "",
  requesterEmail: "",
  dateRequest: "",
  jobTitle: "",
  subsidiary: "",
  costCentre: "",
  department: "",
  prNo: "",
  background: "",
  scopeOfWork: "",
  budgetStatus: "",
  commencementDate: "",
  completionDate: "",
  contractCategory: "",
  contractCategoryName: "",
  region: "",
  regionName: "",
  subRegion: "",
  subRegionName: "",
  procurementApproach: "",
  vendor: "",
  vendorName: "",
};

/**
 * A saved work order's approval authority, as the wizard offered it
 * (GCFO/CFO/CAO/COO). The record and its routing rows carry the API's name
 * for it (GCFOGCOO). Shared by the details page and its PDF.
 */
export const authorityLabelOf = (detail: any, routing: any[] = []): string => {
  const value = detail?.approvalAuthority ?? routing.find((row) => row?.authorityGroup)?.authorityGroup;
  return findApproverGroup(value)?.label ?? value ?? "";
};

/**
 * A work order's uploaded documents, one shape for every reader (details page,
 * Document Versions, PDF). The detail response lists them under any of three
 * names — `documentLinks` as bare URLs, the others as objects.
 */
/**
 * A typed Document Link as an href: http(s) links as they are, anything else
 * (a bare "docs.google.com/…") under https:// — so a link can never become a
 * javascript: or other scheme the browser would run.
 */
export const hrefOf = (url?: string): string | undefined => {
  const text = String(url ?? "").trim();
  if (!text || text === "-") return undefined;
  return /^https?:\/\//i.test(text) ? text : `https://${text}`;
};

/**
 * Approving order in the routing: every reviewer is step 1 (they review side
 * by side); approvers follow from step 2, one step per designation — all the
 * R1s share 2, the R2s 3, and so on, in the order the designations appear.
 */
export const REVIEWER_ORDER = 1;
export const approverOrder = (approvers: { designation?: string }[], index: number) => {
  const key = (approver?: { designation?: string }) => String(approver?.designation ?? "").trim().toUpperCase();
  const designations = approvers.map(key).filter((designation, i, all) => all.indexOf(designation) === i);
  return REVIEWER_ORDER + 1 + designations.indexOf(key(approvers[index]));
};

/**
 * Normalizes Azure Blob URLs for Panel Contract documents in container `spa-be`.
 *
 * In Azure Blob Storage:
 * Storage account: saaspemedia.blob.core.windows.net
 * Container name: spa-be
 * Blob path inside container: spa-be/<envelopeId>/<version>/<documentId>
 * Full URL: https://saaspemedia.blob.core.windows.net/spa-be/spa-be/<envelopeId>/<version>/<documentId>
 */
export const normalizePanelBlobUrl = (rawPathOrUrl: string, id?: string | number, envelopeId?: string): string => {
  if (!rawPathOrUrl) return "";
  let raw = String(rawPathOrUrl).trim();
  const qIndex = raw.indexOf("?");
  let query = "";
  if (qIndex !== -1) {
    query = raw.slice(qIndex);
    raw = raw.slice(0, qIndex);
  }

  let url = raw;
  if (!/^https?:\/\//i.test(url)) {
    const cleanPath = url.replace(/^\/+/, "");
    const fullBlobPath = cleanPath.startsWith("spa-be/") ? cleanPath : `spa-be/${cleanPath}`;
    url = `https://saaspemedia.blob.core.windows.net/spa-be/${fullBlobPath}`;
    if (id !== undefined && id !== null && String(id).trim() !== "" && !url.endsWith(`/${id}`)) {
      url = `${url}/${id}`;
    }
  } else {
    // If it's an absolute URL on saaspemedia.blob.core.windows.net with only one /spa-be/
    url = url.replace(
      /^https:\/\/saaspemedia\.blob\.core\.windows\.net\/spa-be\/(?!spa-be\/)/i,
      "https://saaspemedia.blob.core.windows.net/spa-be/spa-be/"
    );
    if (id !== undefined && id !== null && String(id).trim() !== "" && !url.endsWith(`/${id}`)) {
      url = `${url}/${id}`;
    }
  }

  return query ? `${url}${query}` : url;
};

export const workOrderDocuments = (detail: any) => {
  if (!detail) return [];

  if (Array.isArray(detail)) {
    if (detail.length > 0 && detail[0]?.url && detail[0]?.name) {
      return detail;
    }
  }

  const envelopeId: string =
    detail?.envelopeId ??
    detail?.documentVersion?.envelopeId ??
    detail?.workOrder?.envelopeId ??
    detail?.records?.[0]?.envelopeId ??
    "";

  /* `documentVersionResponses` is where e-POD's detail returns uploaded files;
     `documentVersion.documents` is where a draft's detail returns them (with
     `documentVersion.path` holding the blob container/folder path). */
  const docVer = detail?.documentVersion;
  const docVerList: any[] = Array.isArray(docVer?.documents)
    ? docVer.documents.map((doc: any) => {
        const id = doc.documentId ?? doc.id;
        const numId = parseInt(String(id), 10);
        let rawBlob = doc.blobUrl || docVer?.path;
        if (!rawBlob && envelopeId) {
          rawBlob = `spa-be/${envelopeId}/V1`;
        }
        const blobUrl = rawBlob ? normalizePanelBlobUrl(rawBlob, id, envelopeId) : "";
        return {
          ...doc,
          documentId: id,
          documentName: doc.name ?? doc.documentName ?? doc.fileName,
          name: doc.name ?? doc.documentName ?? doc.fileName,
          blobUrl,
          docVersion: doc.docVersion ?? (numId <= 101 ? 1 : docVer?.docVersion ?? docVer?.versionOrder ?? 0),
        };
      })
    : [];

  const list: any[] =
    [
      detail?.documentVersionResponses,
      docVerList,
      detail?.documents,
      detail?.projectDocuments,
      Array.isArray(detail) ? detail : undefined,
    ].find((value) => Array.isArray(value) && value.length) ?? [];
  return list.map((doc: any, index: number) => {
    /* A stored file opens as blobUrl plus Panel's SAS key — e-POD's key is for
       e-POD's container, and the storage refused Panel's files with it. */
    const key = panelDocVersionKey();
    const docId = typeof doc === "string" ? undefined : doc.documentId ?? doc.id;
    const rawUrl: string =
      typeof doc === "string"
        ? doc
        : doc.url && doc.url.includes("sig=")
        ? doc.url
        : doc.blobUrl || doc.documentLink || doc.url || doc.filePath || "";

    const normalized = normalizePanelBlobUrl(rawUrl, docId, envelopeId);
    let url = normalized;
    if (normalized && normalized.includes(".blob.core.windows.net") && !normalized.includes("sig=")) {
      const cleanKey = key.startsWith("?") ? key : `?${key}`;
      url = `${normalized}${cleanKey}`;
    }

    const name: string =
      (typeof doc === "string" ? "" : doc.documentName ?? doc.name ?? doc.fileName) ||
      url.split("/").pop() ||
      "Document";
    return {
      key: String((typeof doc === "string" ? "" : doc.documentId ?? doc.id) || index),
      id: typeof doc === "string" ? undefined : doc.documentId ?? doc.id,
      name,
      url,
      blobUrl: normalized,
      type: name.toLowerCase().endsWith(".pdf") ? "pdf" : "image",
      docVersion: typeof doc === "string" ? 0 : Number(doc.docVersion ?? doc.versionOrder ?? 0) || 0,
      updatedOn: typeof doc === "string" ? undefined : doc.updatedOn ?? doc.createdOn,
    };
  });
};

/** The id the next uploaded document takes: e-POD starts at 100 and counts up. */
export const nextDocumentId = (detail: any): number => {
  const docs = Array.isArray(detail) ? detail : workOrderDocuments(detail);
  return (
    Math.max(
      99,
      ...docs
        .map((doc: any) => parseInt(String(doc.documentId ?? doc.id), 10))
        .filter((id: number) => !isNaN(id))
    ) + 1
  );
};

/* A record field as text: the first candidate that is set, trimmed. */
const textOf = (...candidates: any[]) =>
  String(candidates.find((value) => value !== undefined && value !== null) ?? "").trim();
/* Like `textOf`, but an empty string is not an answer — it keeps looking. */
const firstFilledText = (...candidates: any[]) => candidates.map((value) => textOf(value)).find(Boolean) ?? "";
const listOf = (value: any): any[] => (Array.isArray(value) ? value : Array.isArray(value?.records) ? value.records : []);

/**
 * A saved work order as the wizard's step-1 values. The record is shaped like
 * WorkOrderRequestDto — `division` for Cost Centre, `category` for the
 * contract category — so each field is read under that name and the form's.
 * Shared by the wizard and the details page's in-place edit.
 */
export const recordToFormValues = (
  record: any,
  current: WorkOrderFormValues = workOrderInitialValues,
  signedIn: { name: string; email: string } = { name: "", email: "" }
): WorkOrderFormValues => ({
  ...current,
  templateId: textOf(record.templateId, current.templateId),
  requesterName: firstFilledText(record.requesterName, current.requesterName, signedIn.name),
  requesterEmail: firstFilledText(record.requesterEmail, current.requesterEmail, signedIn.email),
  dateRequest: (() => {
    /* `moment(undefined)` is NOW, so a record carrying no date at all was
       being shown as today. Blank stays blank. */
    const saved = firstFilledText(record.dateRequest, record.createdOn, current.dateRequest);
    return saved ? moment(saved).format("YYYY-MM-DD") : "";
  })(),
  jobTitle: textOf(record.jobTitle, record.projectTitle),
  subsidiary: textOf(record.subsidiary, record.subsidiaryName),
  costCentre: textOf(record.division, record.divisionName, record.costCentre),
  department: textOf(record.department),
  prNo: textOf(record.prNo),
  background: textOf(record.background),
  scopeOfWork: textOf(record.scopeOfWork),
  budgetStatus: textOf(record.budgetStatus),
  commencementDate: textOf(record.commencementDate),
  completionDate: textOf(record.completionDate),
  /* The pickers hold ids and show the companion name, so a record that
     carries only labels populates both halves from the label. */
  contractCategory: textOf(record.categoryId, record.category, record.contractCategory),
  contractCategoryName: textOf(record.category, record.contractCategory),
  region: textOf(record.regionId, record.region),
  regionName: textOf(record.region),
  subRegion: textOf(record.subRegionId, record.subRegion),
  subRegionName: textOf(record.subRegion),
  procurementApproach: textOf(record.procurementApproach),
  vendor: textOf(record.vendorId, record.vendorName, record.vendor),
  vendorName: textOf(record.vendorName, record.vendor),
});

/** A saved work order's SOR lines, as the SOR step holds them. */
export const recordToSorItems = (record: any): SorItem[] =>
  listOf(record.sorDetails ?? record.sorItems).map((line: any, index: number) => ({
    key: `sor-${index}`,
    sorId: textOf(line.sorId),
    itemName: textOf(line.itemName),
    description: textOf(line.description),
    uom: textOf(line.uom),
    quantity: Number(line.quantity) || 0,
    rate: Number(line.rate) || 0,
    amount: Number(line.amount) || 0,
  }));

/** A saved work order's document links, as the Project Documents step holds
    them — the API's linkTitle / link, or the older documentName / documentLink. */
export const recordToLinks = (record: any): DocumentLinkEntry[] =>
  listOf(record.documentLinkDetails ?? record.documentLinks)
    .filter((link: any) => typeof link === "object")
    .map((link: any, index: number) => ({
      key: `link-${index}`,
      documentName: firstFilledText(link.linkTitle, link.documentName, link.name),
      documentLink: firstFilledText(link.link, link.documentLink, link.url),
    }));

/** The links as `documentLinkDetails` takes them — `linkTitle` / `link` — blank rows dropped.
    Shared by create, draft-create and edit. */
export const linksPayload = (links: DocumentLinkEntry[]) =>
  links
    .filter((link) => link.documentName || link.documentLink)
    .map((link) => ({ linkTitle: link.documentName, link: link.documentLink }));

/**
 * One comment-trail row, whichever spelling it came back in — the commenter* /
 * comment names, or e-POD's (reviewerName, commentedBy, versionOrder, message).
 * The Comments tab and the work order PDF both read it through here.
 */
/* What the backend writes into the trail by itself when an approval or
   rejection carries no comment — "Approved by sasikumar.m (R1)". Not a comment. */
const AUTO_COMMENT = /^(approved|rejected) by .* \([^()]*\)$/i;

export const commentFields = (row: any) => {
  const first = (...values: any[]) =>
    values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");
  const comment = first(row?.comment, row?.message);
  return {
    name: first(row?.commenterName, row?.reviewerName, row?.userName),
    email: first(row?.commenterEmail, row?.commentedBy, row?.userEmail, row?.email),
    documentName: first(row?.documentName),
    version: first(row?.documentVersion, row?.versionOrder),
    comment: comment && !AUTO_COMMENT.test(String(comment).trim()) ? comment : undefined,
    commentedOn: first(row?.commentedOn),
  };
};

/**
 * Saves a stored document under its own name, as e-POD's download does: the
 * file is fetched and handed over as a blob, since a cross-origin link ignores
 * `download`. If the fetch is refused, the file opens in a new tab instead.
 */
export const downloadDocument = async (url: string, name: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(String(response.status));
    const href = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = href;
    link.download = /\.pdf$/i.test(name) ? name : `${name}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
  } catch {
    window.open(url, "_blank", "noopener");
  }
};

/**
 * Why a work order was rejected or cancelled, read the way e-POD reads it:
 * `rejectReason` / `cancelReason` on the detail — for a rejection, else the
 * comment on the routing step that rejected it.
 */
export const decisionReasons = (detail: any, routing: any[] = []) => {
  const code = (value: any) => String(value ?? "").trim().toUpperCase();
  const is = (status: string) => [detail?.status, detail?.overallStatus].some((value) => code(value) === status);
  const rejectedStep = routing.find((row) => code(row?.status) === "REJECTED");
  return {
    isRejected: is("REJECTED"),
    isCancelled: is("CANCELLED"),
    rejectReason: firstFilledText(detail?.rejectReason, detail?.rejectionReason, rejectedStep?.comment, rejectedStep?.rejectReason),
    cancelReason: firstFilledText(detail?.cancelReason, detail?.cancellationReason),
  };
};
